import outdent from "outdent";
import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

export const microsoftIntune = defineConnector({
  identifier: "microsoftIntune",
  name: "Microsoft Intune",
  tagLine: "Endpoint and device management",
  logo,
  protocol: "http",
  tags: ["security", "it"],
  defaultUrlPattern: () => "^https://graph\\.microsoft\\.com/.*",
  auth: oauthAuth,
  steps: [
    {
      title: "Register an app in Azure",
      items: [
        {
          kind: "markdown",
          content: outdent`
            1. Navigate to your [Azure app registrations](https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade) and select **New Registration**
            2. **Name**: Give your app a descriptive name (e.g. "3B Intune")
            3. **Supported account types**: Select "Single tenant only"
            4. Select **Register**
          `,
        },
      ],
    },
    {
      title: "Get client ID and tenant ID",
      items: [
        {
          kind: "markdown",
          content:
            "Navigate to the **Overview** of your created app and copy the following values:",
        },
        {
          kind: "input",
          label: "Application (client) ID",
          target: { kind: "oauthConnection", field: "clientId" },
          placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
        },
        {
          kind: "input",
          label: "Directory (tenant) ID",
          target: { kind: "configuration", field: "tenantId" },
          placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
        },
      ],
    },
    {
      title: "Create client secret",
      items: [
        {
          kind: "markdown",
          content: outdent`
            1. Select **Add a certificate or secret** from the overview page, then select **+ New client secret**
            2. Enter a description and select your preferred expiry
            3. Select **Add** and copy the **value** of the secret
          `,
        },
        {
          kind: "input",
          label: "Client secret value",
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
    {
      title: "Add Intune API permissions",
      items: [
        {
          kind: "markdown",
          content: outdent`
            1. Navigate to **API permissions** and select **+ Add a permission**
            2. Select the **Microsoft Graph** API
            3. Select **Application permissions**
            4. Add the Intune permissions you need, such as:
               - \`DeviceManagementManagedDevices.Read.All\`
               - \`DeviceManagementConfiguration.Read.All\`
               - \`DeviceManagementApps.Read.All\`
            5. Select **Add permissions**, then grant **admin consent**
          `,
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "client_credentials",
    tokenEndpoint: ({ tenantId }) =>
      `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/token`,
    scopes: () => ["https://graph.microsoft.com/.default"],
    clientCredentialsFrom: "db",
    tokenEndpointAuthMethod: () => "client_secret_post",
  },
  connectionTest: {
    protocol: "http",
    request: () => ({
      url: "https://graph.microsoft.com/v1.0/deviceManagement",
      expectedStatus: [200, 403],
    }),
  },
});
